import { createClient } from '@supabase/supabase-js'
import { User, UserRole } from '../types'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    storageKey: 'sb-auth-token',
    flowType: 'pkce'
  },
  global: {
    headers: {
      'x-application-name': 'insightflow-app',
    },
  },
  db: {
    schema: 'public',
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
})

// Database types based on PRD requirements
export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string
          email: string
          full_name: string
          role: 'coach' | 'coachee' | 'admin'
          avatar_url: string | null
          profile_picture_url: string | null
          profile_picture_visibility: 'public' | 'private' | 'none' | null
          specializations: string[] | null
          contact_details: string | null
          coach_id: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id: string
          email: string
          full_name: string
          role: 'coach' | 'coachee' | 'admin'
          avatar_url?: string | null
          profile_picture_url?: string | null
          profile_picture_visibility?: 'public' | 'private' | 'none' | null
          specializations?: string[] | null
          contact_details?: string | null
          coach_id?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          email?: string
          full_name?: string
          role?: 'coach' | 'coachee' | 'admin'
          avatar_url?: string | null
          profile_picture_url?: string | null
          profile_picture_visibility?: 'public' | 'private' | 'none' | null
          specializations?: string[] | null
          contact_details?: string | null
          coach_id?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      coaching_sessions: {
        Row: {
          id: string
          coach_id: string
          coachee_id: string
          title: string
          date: string
          start_time: string
          duration_minutes: number
          notes: string | null
          action_items: string[] | null
          observations: string | null
          tags: string[] | null
          challenges_addressed: string[] | null
          status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          coach_id: string
          coachee_id: string
          title: string
          date: string
          start_time: string
          duration_minutes: number
          notes?: string | null
          action_items?: string[] | null
          observations?: string | null
          tags?: string[] | null
          challenges_addressed?: string[] | null
          status?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          coach_id?: string
          coachee_id?: string
          title?: string
          date?: string
          start_time?: string
          duration_minutes?: number
          notes?: string | null
          action_items?: string[] | null
          observations?: string | null
          tags?: string[] | null
          challenges_addressed?: string[] | null
          status?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
          created_at?: string
          updated_at?: string
        }
      }
      reflection_entries: {
        Row: {
          id: string
          coachee_id: string
          date: string
          mood: number | null
          thoughts: string | null
          insights: string | null
          challenges: string | null
          potential_solutions: string | null
          tags: string[] | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          coachee_id: string
          date: string
          mood?: number | null
          thoughts?: string | null
          insights?: string | null
          challenges?: string | null
          potential_solutions?: string | null
          tags?: string[] | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          coachee_id?: string
          date?: string
          mood?: number | null
          thoughts?: string | null
          insights?: string | null
          challenges?: string | null
          potential_solutions?: string | null
          tags?: string[] | null
          created_at?: string
          updated_at?: string
        }
      }
      goals: {
        Row: {
          id: string
          user_id: string
          title: string
          description: string | null
          category: string
          target_date: string | null
          status: 'active' | 'completed' | 'paused'
          progress: number
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          title: string
          description?: string | null
          category: string
          target_date?: string | null
          status?: 'active' | 'completed' | 'paused'
          progress?: number
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          title?: string
          description?: string | null
          category?: string
          target_date?: string | null
          status?: 'active' | 'completed' | 'paused'
          progress?: number
          created_at?: string
          updated_at?: string
        }
      }
      tasks: {
        Row: {
          id: string
          user_id: string
          title: string
          description: string | null
          due_date: string | null
          completed: boolean
          completed_at: string | null
          goal_id: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          title: string
          description?: string | null
          due_date?: string | null
          completed?: boolean
          completed_at?: string | null
          goal_id?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          title?: string
          description?: string | null
          due_date?: string | null
          completed?: boolean
          completed_at?: string | null
          goal_id?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      messages: {
        Row: {
          id: string
          sender_id: string
          recipient_id: string
          type: 'message' | 'assignment' | 'feedback'
          title: string | null
          content: string
          read: boolean
          read_at: string | null
          created_at: string
        }
        Insert: {
          id?: string
          sender_id: string
          recipient_id: string
          type: 'message' | 'assignment' | 'feedback'
          title?: string | null
          content: string
          read?: boolean
          read_at?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          sender_id?: string
          recipient_id?: string
          type?: 'message' | 'assignment' | 'feedback'
          title?: string | null
          content?: string
          read?: boolean
          read_at?: string | null
          created_at?: string
        }
      }
      coach_coachee_relationships: {
        Row: {
          id: string
          coach_id: string
          coachee_id: string
          status: 'active' | 'inactive' | 'pending'
          started_at: string
          ended_at: string | null
          notes: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          coach_id: string
          coachee_id: string
          status?: 'active' | 'inactive' | 'pending'
          started_at: string
          ended_at?: string | null
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          coach_id?: string
          coachee_id?: string
          status?: 'active' | 'inactive' | 'pending'
          started_at?: string
          ended_at?: string | null
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
      }
    }
  }
}

// Helper functions for user management
export const createUserProfile = async (user: any, role: UserRole, additionalData?: any) => {
  const profileData = {
    id: user.id,
    email: user.email,
    full_name: additionalData?.full_name || user.user_metadata?.full_name || '',
    role,
    ...additionalData
  }
  
  console.log('createUserProfile - inserting data:', profileData)
  
  const { data, error } = await supabase
    .from('profiles')
    .insert(profileData)
    .select()
    .single()

  if (error) {
    console.error('createUserProfile - error:', error)
    throw error
  }
  
  console.log('createUserProfile - success:', data)
  return data
}

export const getUserProfile = async (userId: string): Promise<User | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()

  if (error) {
    console.error('Error fetching user profile:', error)
    return null
  }

  return data as User
} 